// vg-binding: P7.D-35
//
// apps/vendor/src/features/analytics/api.ts

import { csrfFetch } from '../../lib/api/csrfFetch';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

export interface AnalyticsKpi {
  id: string;
  label: string;
  value: number;
  unit: string;
  direction: 'higher_better' | 'lower_better';
  data_source: 'live' | 'placeholder';
}

export interface TimeseriesPoint {
  date: string;
  value: number;
}

export interface OrdersByStatusBucket {
  status: string;
  count: number;
}

export interface TopProductRow {
  product_id: string;
  name: string;
  sku: string | null;
  slug: string | null;
  image_url: string | null;
  blueprint_code: string | null;
  category: string | null;
  status: string | null;
  units_sold: number;
  orders: number;
  revenue_cents: number;
}

interface ApiOk<T> { ok: true; data: T }
interface ApiErr { ok: false; error: { code: string; message: string } }

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) throw new Error(body.error.message);
  return body.data;
}

export function useKpis(): UseQueryResult<{ kpis: AnalyticsKpi[] }, Error> {
  return useQuery({
    queryKey: ['vendor', 'analytics', 'kpis'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/analytics/kpis', { credentials: 'include' });
      return unwrap<{ kpis: AnalyticsKpi[] }>(res);
    },
    refetchInterval: 60 * 1000,
  });
}

export function useTimeseries(days = 14): UseQueryResult<
  { rows: TimeseriesPoint[]; data_source: string },
  Error
> {
  return useQuery({
    queryKey: ['vendor', 'analytics', 'timeseries', days],
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/analytics/timeseries?days=${days}`,
        { credentials: 'include' },
      );
      return unwrap<{ rows: TimeseriesPoint[]; data_source: string }>(res);
    },
  });
}

export function useOrdersByStatus(): UseQueryResult<
  { buckets: OrdersByStatusBucket[]; data_source: string },
  Error
> {
  return useQuery({
    queryKey: ['vendor', 'analytics', 'orders-by-status'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/analytics/orders-by-status', {
        credentials: 'include',
      });
      return unwrap<{ buckets: OrdersByStatusBucket[]; data_source: string }>(res);
    },
  });
}

export function useTopProducts(limit = 5): UseQueryResult<
  { rows: TopProductRow[]; data_source: string },
  Error
> {
  return useQuery({
    queryKey: ['vendor', 'analytics', 'top-products', limit],
    queryFn: async () => {
      const res = await csrfFetch(`/api/v1/vendor/analytics/top-products?limit=${limit}`, {
        credentials: 'include',
      });
      return unwrap<{ rows: TopProductRow[]; data_source: string }>(res);
    },
  });
}
